import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { Typography, Button, AlertColor } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { CollectorInstallError, GroupTypeDetails } from 'graphql/types';
import { EnabledDisabled } from 'modules/Settings/constant';
import { useScrollbarStyles } from 'modules/common/styles';
import { CollectorInstallInfo } from 'modules/Collectors/CollectorsListPage';
import { useFeatureFlag } from 'modules/common/hooks';
import { useSidePanelContext } from 'modules/rootPage';
import { useMessage } from 'modules/intl';
import { CollectorErrorList } from '../CollectorErrorList';
import { CollectorErrorListUnified } from '../CollectorErrorListUnified';
import { ToastSnackBar } from '../../../Settings/ToastSnackBar';
import { GroupsResult } from './hooks';
import { DefaultSettingsRow } from './DefaultSettingsRow';
import { SettingsRow } from './SettingsRow';
import { CollectorRowSettings, FormData } from './hooks';

type CollectorSettingsBodyProps = {
  onClose?: () => void;
  collectorName?: string;
  enabledGroupTypes: GroupTypeDetails[];
  defaultEnabled: EnabledDisabled;
  errors?: Array<CollectorInstallError | null | undefined> | null;
  installInfo?: CollectorInstallInfo;
  defaultOptedIn: EnabledDisabled;
  prepend: UseFieldArrayReturn['prepend'];
  remove: UseFieldArrayReturn['remove'];
  handleSubmit: UseFormReturn['handleSubmit'];
  loading?: boolean;
  fields: Record<string, any>[];
  fieldName: string;
  onSave: (note: FormData) => Promise<void>;
  installId?: string;
  retryUrl?: string;
  isDirty: boolean;
};

type StyleProps = {
  rows: number;
  isTiwUnifiedCollectorsTab: boolean;
};

const useStyles = makeStyles((theme) => ({
  actions: {
    justifyContent: 'flex-end',
    display: 'flex',
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(1)
  },
  rightContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(2.5),
    width: '100%'
  },
  actionButton: {
    margin: ({ isTiwUnifiedCollectorsTab }: StyleProps) =>
      !isTiwUnifiedCollectorsTab ? theme.spacing(1) : theme.spacing(3, 0, 0, 0)
  },
  footer: {
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'column',
    height: ({ rows }: StyleProps) => (rows ? 'auto' : '100%')
  },
  spacer: {
    flexGrow: 1,
    flexShrink: 1
  },
  settingsRowContent: {
    marginTop: theme.spacing(2),
    alignContent: 'flex-start',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 300px)',
    width: '100%'
  },
  title: {
    margin: ({ isTiwUnifiedCollectorsTab }: StyleProps) =>
      !isTiwUnifiedCollectorsTab
        ? theme.spacing(2, 0.5, 0.5, 0.5)
        : theme.spacing(1.75, 0, 0, 0)
  },
  errorWidth: {
    width: '98%'
  }
}));

export const CollectorSettingsBody: React.FC<CollectorSettingsBodyProps> = ({
  onClose,
  loading,
  enabledGroupTypes,
  defaultEnabled,
  defaultOptedIn,
  prepend,
  installInfo,
  errors,
  remove,
  collectorName,
  fieldName,
  fields,
  onSave,
  handleSubmit,
  installId,
  retryUrl,
  isDirty
}: CollectorSettingsBodyProps) => {
  const classesScrollbar = useScrollbarStyles();
  const isTiwUnifiedCollectorsTab = useFeatureFlag('isTiwUnifiedCollectorsTab');
  const handleClose = useCallback(() => {
    onClose && !isTiwUnifiedCollectorsTab ? onClose() : undefined;
  }, [isTiwUnifiedCollectorsTab, onClose]);
  const classes = useStyles({
    isTiwUnifiedCollectorsTab,
    rows: fields.length
  });

  const { pinned } = useSidePanelContext();
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    if (enabledGroupTypes && enabledGroupTypes.length) {
      const values = enabledGroupTypes.reduce((a, c) => {
        a[c.value] = [];
        return a;
      }, {} as GroupsResult);
      prepend({
        ...values,
        ...{
          enableDisabled: defaultEnabled,
          optedIn: defaultOptedIn
        }
      });
    }
  }, [enabledGroupTypes, prepend, defaultEnabled, defaultOptedIn]);

  const handleRemove = useCallback(
    (rowId) => {
      remove(rowId);
    },
    [remove]
  );

  const onSaveClick = useCallback(async () => {
    await handleSubmit(onSave)();
    setIsSnackBarOpen(true);
  }, [handleSubmit, onSave]);

  const titleValue = useMemo(() => ({ prefix: collectorName }), [
    collectorName
  ]);

  const collectorSaveSuccessMessage = useMessage(
    'collectorSetting.collectorSaveSuccessMessage'
  );
  const category = 'success' as AlertColor;
  return (
    <div className={classes.rightContainer}>
      {installId && isTiwUnifiedCollectorsTab && (
        <div className={classes.errorWidth}>
          <CollectorErrorListUnified
            data-testid="collectorDataList-testid"
            errors={errors}
            retryURL={retryUrl || ''}
            installId={installId}
          />
        </div>
      )}
      {!isTiwUnifiedCollectorsTab ? (
        <Typography variant="h4" className={classes.title}>
          <FormattedMessage id="collectorSetting.title" values={titleValue} />
        </Typography>
      ) : (
        <Typography variant="h4" className={classes.title}>
          <FormattedMessage id="collectorSetting.companyCollectorTitle" />
        </Typography>
      )}

      <div
        className={classnames(
          classes.settingsRowContent,
          classesScrollbar.scrollbar
        )}
        data-testid="setting-body"
      >
        {Boolean(enabledGroupTypes.length) &&
          fields.map((field, index) => (
            <SettingsRow
              key={field.id}
              rowID={`${fieldName}.${index}`}
              rowIndex={index}
              field={field as CollectorRowSettings}
              onRemove={handleRemove}
              enabledGroupTypes={enabledGroupTypes}
            />
          ))}
      </div>

      <DefaultSettingsRow
        defaultEnabled={defaultEnabled}
        defaultOptedIn={defaultOptedIn}
        handleClick={handleClick}
        disabled={!enabledGroupTypes.length}
      />

      {installInfo?.installId && !isTiwUnifiedCollectorsTab && (
        <CollectorErrorList
          data-testid="collectorDataList-testid"
          errors={errors}
          collectorId={installInfo?.collectorItem?.id}
          retryURL={installInfo?.collectorItem?.retryURL}
          installId={installInfo?.installId}
        />
      )}
      <div className={classes.spacer} />
      {!isTiwUnifiedCollectorsTab ? (
        <div className={classes.actions}>
          <Button
            onClick={handleClose}
            className={classes.actionButton}
            data-testid="cancel-button"
          >
            <FormattedMessage id="collectorSetting.cancel" />
          </Button>
          <Button
            className={classes.actionButton}
            disabled={loading || !isDirty}
            color="primary"
            variant="contained"
            onClick={onSaveClick}
            data-testid="save-button"
          >
            <FormattedMessage id="collectorSetting.save" />
          </Button>
        </div>
      ) : (
        <div>
          <Button
            className={classes.actionButton}
            disabled={loading || !isDirty}
            color="primary"
            variant="contained"
            onClick={onSaveClick}
            data-testid="save-button"
          >
            <FormattedMessage id="collectorSetting.save" />
          </Button>
          <ToastSnackBar
            show={isSnackBarOpen}
            category={category}
            message={collectorSaveSuccessMessage}
            onClose={onSnackbarClose}
            pinned={pinned as boolean}
          />
        </div>
      )}
    </div>
  );
};
